import * as React from 'react';
import { Alert, Button, DatePicker, formatDuration, Panel } from '@watcherr/ui';
import { t } from '~/i18n';
import { Profile } from '@watcherr/resources';
import { useQuery } from '@tanstack/react-query';
import { addDays, endOfWeek, endOfYesterday, startOfDay, startOfWeek } from 'date-fns';
import { LocationStatsChart } from '~components/Charts';
import styled from 'styled-components';
import { getCategory } from '~components/Charts/zoneCategories.utils';
import { ExportModal } from '~containers/Alerts/AlertsExportModal';
import {
  fetchLocationStatAggregates,
  fetchLocationStatSessions,
  LocationStatsAggregate,
  LocationStatsSession,
} from './location-stats.api';
import { exportSessions } from './export-sessions';
import { endOfToday } from 'date-fns';

interface ResidentActivityContainerProps {
  residentId: number;
}

const ResidentActivityContainer: React.FC<ResidentActivityContainerProps> = (props) => {
  const { data: resident, isLoading: residentLoading } = useQuery({
    queryKey: ['profile', props.residentId],
    queryFn: () => Profile.getProfile(props.residentId),
  });

  const yesterday = React.useMemo(() => endOfYesterday(), []);
  const [selected, setSelected] = React.useState<Date | undefined>(yesterday);

  const start = React.useMemo(() => startOfDay(selected ?? new Date()), [selected]);
  const end = React.useMemo(() => addDays(start, 1), [selected]);
  const weekStart = React.useMemo(() => startOfWeek(selected ?? new Date()), [selected]);
  const { data: slots, isLoading: slotsLoading } = useQuery({
    queryKey: ['location-sessions', props.residentId, start.toDateString()],
    queryFn: () => fetchLocationStatSessions(props.residentId, start, end),
  });
  const { data: aggregates, isLoading: aggregatesLoading } = useQuery({
    queryKey: ['location-aggregates', props.residentId, weekStart.toDateString()],
    queryFn: () => fetchLocationStatAggregates(props.residentId, weekStart, endOfWeek(start)),
  });

  const [showExportModal, setShowExportModal] = React.useState(false);
  const [showChart, setShowChart] = React.useState(false);

  const isLoading = residentLoading || slotsLoading || aggregatesLoading;

  React.useEffect(() => {
    if (!isLoading && slots && slots.length > 0) {
      // Delay chart rendering to avoid suspension
      React.startTransition(() => {
        setShowChart(true);
      });
    } else {
      setShowChart(false);
    }
  }, [isLoading, slots]);

  const currentDay = React.useMemo(() => endOfToday(), []);

  return (
    <>
      <Panel.Section>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'relative',
            zIndex: 10000,
          }}
        >
          <Panel.Title></Panel.Title>
          <div>
            <DatePicker mode="single" selected={selected} onSelect={setSelected} hidden={{ after: currentDay }} />
            <Button style={{ marginLeft: 17 }} onClick={() => setShowExportModal(true)}>
              {'Export'}
            </Button>
          </div>
        </div>
        {isLoading ? (
          <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Alert type="message">{t('global.loading')}</Alert>
          </div>
        ) : !slots?.length ? (
          <div style={{ height: 400 }}>
            <Alert type="message">{t('residentDetail.location.stats.empty')}</Alert>
          </div>
        ) : (
          <div>
            {showChart && slots && <LocationStatsChart onMouseEnter={console.log} slots={slots} width={350} />}
            {slots && aggregates && (
              <Activities name={resident?.firstName ?? ''} slots={slots} aggregates={aggregates} />
            )}
          </div>
        )}
        {showExportModal && (
          <ExportModal
            exportHandler={(start, end) => exportSessions(props.residentId, start, end)}
            maxDays={10}
            onClose={() => setShowExportModal(false)}
          />
        )}
      </Panel.Section>
    </>
  );
};

const AGGREGATE_DAYS = 7;

export default ResidentActivityContainer;

export function Activities({
  name,
  slots,
  aggregates,
}: {
  name: string;
  slots: LocationStatsSession[];
  aggregates: LocationStatsAggregate[];
}) {
  return (
    <LocationCardsWrapper>
      {aggregates
        .filter((x) => x.category)
        .map((stat) => {
          const { color, icon, Component } = getCategory(stat.category as any);
          const msLastDay = slots
            .filter((x) => x.category === stat.category)
            .reduce((acc, x) => acc + x.seconds * 1000, 0);
          const secondsLastWeek = stat.seconds ?? 0;
          const total = formatDuration(msLastDay);
          const daily = formatDuration((secondsLastWeek * 1000) / AGGREGATE_DAYS);
          const category = t(`zone.category.${stat.category}`);
          return (
            <LocationCard key={stat.category}>
              <LocationCardContent>
                <IconWrapper>
                  <IconBacking style={{ backgroundColor: color }} />
                  {Component ? <Component size={20} color={color} /> : <Icon style={{ color: color }}>{icon}</Icon>}
                </IconWrapper>
                <LocationCardDetails>
                  <p style={{ color: color }}>{category}</p>
                  <p>
                    {t('residentDetail.location.stats.category.description.multipleDays', {
                      name,
                      total,
                      category: category.toLowerCase(),
                      daily,
                    })}
                  </p>
                </LocationCardDetails>
              </LocationCardContent>
            </LocationCard>
          );
        })}
    </LocationCardsWrapper>
  );
}

const IconWrapper = styled.div`
  font-size: 20px;
  padding: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  margin-right: 15px;
`;

const IconSize = 40;

const IconBacking = styled.div`
  opacity: 0.1;
  height: ${IconSize}px;
  width: ${IconSize}px;
  border-radius: ${IconSize / 2}px;
  position: absolute;
`;
const Icon = styled.i`
  font-family: symbols-watcherr;
  font-size: 24px;
`;

const LocationCardsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: 30px;
  width: 100%;
`;

const LocationCard = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  max-width: 50%;
`;

const LocationCardContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  background: #efefef66;
  margin: 10px;
  padding: 15px;
  border-radius: 4px;
`;

const LocationCardDetails = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;
